import { useCallback } from 'react';

import { Box, Header, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { TableHeader } from 'components/nutrition/v2/table-header';
import Picture from 'components/picture';
import { useLocale } from 'state/intl';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { getKeysWithoutTypename, mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { getUnitForNutrient } from 'utils/menu';

import { ItemAllergens } from './item-allergens';
import { ItemIngredients } from './item-ingredients-information';
import { NutrientText } from './styled';
import { IngredientsType } from './types';

type ItemNutritionInformationProps = {
  item: ILocalizedMenuEntry;
};

export const ItemNutritionInformation = ({ item }: ItemNutritionInformationProps) => {
  const { formatMessage, formatNumber } = useIntl();
  const { ingredients } = useUnifiedMenuContext();
  const { language } = useLocale();

  // This is used to check if the item is a non-food item
  // we don't want to show the per serving header and nutrients for non-food items
  const isNoNFood = item.productType[0] === 'NON_FOOD';

  // This function is used to find the ingredients for the item
  const findIngredients = useCallback(() => {
    const finalIngredients = item.ingredients?.reduce((acc, ingredientEntry) => {
      const found = ingredients?.find(ingredient => ingredient.id === ingredientEntry.entryId);
      if (found) {
        acc.push(found);
      }
      return acc;
    }, []);

    const finalIngredientsWithComponents = finalIngredients?.map(ingredient => ({
      name: ingredient?.name[language] || ingredient?.name?.en || '',
      ingredients: ingredient?.components[language] || ingredient?.components?.en || [],
    }));
    return (finalIngredientsWithComponents || []) as IngredientsType[];
  }, [item.ingredients, ingredients, language]);

  const nutritionLabels = getKeysWithoutTypename(item.nutrition?.nutrients || {}).filter(
    label => !!item.nutrition?.nutrients?.[label]
  );

  return (
    <Box>
      <Picture
        image={mapImageResourceToSanityImage(item.image?.resource ?? '')}
        alt="itemImage"
        objectFitContain
        width={300}
        height={300}
        alignSelf="center"
      />
      <Box marginBottom="$4">
        <Header textAlign="center" focusable>
          {item.name?.locale}
        </Header>
        {!isNoNFood && (
          <TableHeader testID="per-serving-header" title={formatMessage({ id: 'perServing' })} />
        )}
        {!isNoNFood && (
          <NutrientText key="calories">
            <Text textTransform="capitalize">{formatMessage({ id: 'us-calories' })}</Text>
            <Text>
              {`${formatNumber(item.nutrition?.calories?.def!, {
                maximumFractionDigits: 1,
              })} ${getUnitForNutrient('calories')}`}
            </Text>
          </NutrientText>
        )}
        {nutritionLabels.map(label => (
          <NutrientText key={label}>
            <Text textTransform="capitalize">{formatMessage({ id: 'us-' + label })}</Text>
            <Text>
              {`${formatNumber(item.nutrition!.nutrients![label]!, {
                maximumFractionDigits: 1,
              })} ${getUnitForNutrient(label)}`}
            </Text>
          </NutrientText>
        ))}
        <ItemIngredients ingredients={findIngredients()} />
        {item.nutrition?.allergens && <ItemAllergens item={item} />}
      </Box>
    </Box>
  );
};
